import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, SimpleGrid, Group, Stack, Badge, Progress,
  SegmentedControl, Skeleton, Table, ScrollArea, Center,
} from '@mantine/core'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import {
  IconPackage, IconAlertTriangle, IconTrendingUp, IconTrendingDown,
  IconTicket, IconBottle,
} from '@tabler/icons-react'
import { useBreakpoint } from '../hooks/useBreakpoint'
import {
  getInventorySummary, getLowStockItems,
  getStockDailyUsage, getStockMonthlyUsage,
  getStockCostByWO, getStockCostByHK,
} from '../api/client'

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 0 })
const fmtCost = (n: number) =>
  n >= 1000 ? `฿${(n / 1000).toFixed(1)}k` : `฿${n.toFixed(0)}`

function StatCard({ label, value, icon: Icon, color, sub, onClick }: {
  label: string; value: any; icon: any; color: string; sub?: string; onClick?: () => void
}) {
  return (
    <Box onClick={onClick}
      style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 16px 16px', cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = color }}
      onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = '#e2e8f0' }}
    >
      <Box style={{ width: 60, height: 60, borderRadius: '50%', background: color + '15', border: `2px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={26} color={color} />
      </Box>
      {value !== undefined ? (
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 700, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>
          {value}
        </Text>
      ) : <Skeleton width={60} height={32} mt={4} />}
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, letterSpacing: '0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
        {label}
      </Text>
      {sub && <Text size="xs" c="dimmed" ta="center">{sub}</Text>}
    </Box>
  )
}

export default function StockDashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile, isSmall } = useBreakpoint()

  const [summary, setSummary]     = useState<any>(null)
  const [lowStock, setLowStock]   = useState<any[] | null>(null)
  const [dailyUsage, setDailyUsage]     = useState<any[] | null>(null)
  const [monthlyUsage, setMonthlyUsage] = useState<any[] | null>(null)
  const [costWO, setCostWO]   = useState<any[] | null>(null)
  const [costHK, setCostHK]   = useState<any[] | null>(null)
  const [period, setPeriod]   = useState<'30' | '90'>('30')

  useEffect(() => {
    getInventorySummary().then(r => setSummary(r.data)).catch(() => {})
    getLowStockItems().then(r => setLowStock(r.data ?? [])).catch(() => setLowStock([]))
    getStockCostByWO(30).then(r => setCostWO(r.data ?? [])).catch(() => setCostWO([]))
    getStockCostByHK(30).then(r => setCostHK(r.data ?? [])).catch(() => setCostHK([]))
  }, [])

  useEffect(() => {
    setDailyUsage(null)
    setMonthlyUsage(null)
    const days = +period
    getStockDailyUsage(days).then(r => setDailyUsage(r.data ?? [])).catch(() => setDailyUsage([]))
    getStockMonthlyUsage(12).then(r => setMonthlyUsage(r.data ?? [])).catch(() => setMonthlyUsage([]))
  }, [period])

  const maxCost = Math.max(...(costWO ?? []).map(r => r.totalCost), ...(costHK ?? []).map(r => r.totalCost), 1)

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb="xl">
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('stock.dashTitle', 'แดชบอร์ดคลังสินค้า')}
        </Text>
        <Text size="sm" c="dimmed" style={{}} >{t('stock.dashSubtitle', 'ภาพรวมสต็อก การใช้งาน และต้นทุน')}</Text>
      </Box>

      {/* ── KPI Row ── */}
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
        <StatCard label={t('stock.totalItems', 'รายการทั้งหมด')} value={summary?.totalItems} icon={IconPackage} color="#0d9488"
          onClick={() => navigate('/inventory')} />
        <StatCard label={t('stock.totalValue', 'มูลค่ารวม')} value={summary ? `฿${fmt(summary.totalValue)}` : undefined} icon={IconTrendingUp} color="#3b82f6" />
        <StatCard label={t('stock.lowStock', 'ใกล้หมด')} value={summary?.lowStockCount} icon={IconAlertTriangle} color="#f59e0b"
          sub={t('stock.lowStockSub', '≤ minimum stock')} onClick={() => navigate('/inventory?lowStock=true')} />
        <StatCard label={t('stock.outOfStock', 'หมดสต็อก')} value={summary?.outOfStockCount} icon={IconAlertTriangle} color="#ef4444"
          onClick={() => navigate('/inventory?lowStock=true')} />
      </SimpleGrid>

      {/* ── Low Stock Items ── */}
      {(lowStock === null || (lowStock?.length ?? 0) > 0) && (
        <Box mb="xl" style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
          <Group mb={12} gap={8}>
            <IconAlertTriangle size={16} color="#f59e0b" />
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t('stock.lowStockItems', 'รายการที่ใกล้หมด/หมดแล้ว')}</Text>
          </Group>
          {lowStock === null ? <Skeleton height={80} /> : (
            <ScrollArea h={200}>
              <Stack gap={6}>
                {lowStock.map((i: any) => (
                  <Group key={i.id} justify="space-between" gap={8}>
                    <Text size="sm" lineClamp={1} style={{ flex: 1 }}>{i.name}</Text>
                    <Group gap={6}>
                      <Text size="sm" fw={600} c={i.stockQty === 0 ? 'red' : 'orange'}>{i.stockQty}</Text>
                      <Text size="xs" c="dimmed">/ {i.minStock} {i.unit}</Text>
                      <Badge size="xs" color={i.stockQty === 0 ? 'red' : 'orange'} variant="light">
                        {i.stockQty === 0 ? t('stock.outBadge', 'หมด') : t('stock.lowBadge', 'ใกล้หมด')}
                      </Badge>
                    </Group>
                  </Group>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Box>
      )}

      {/* ── Usage Charts ── */}
      <Box mb="xl" style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
        <Group justify="space-between" mb={16}>
          <Group gap={8}>
            <IconTrendingDown size={16} color="#0f172a" />
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t('stock.usageChart', 'การเคลื่อนไหวสต็อก')}</Text>
          </Group>
          <SegmentedControl size="xs" value={period} onChange={(v) => setPeriod(v as '30' | '90')}
            data={[{ value: '30', label: '30 วัน' }, { value: '90', label: '90 วัน' }]} />
        </Group>
        {dailyUsage === null ? <Skeleton height={220} /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyUsage} margin={{ top: 5, right: 0, left: -30, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={(v) => v?.slice(5) ?? ''} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Tooltip labelFormatter={(v) => `วันที่ ${v}`}
                formatter={(v: any, name: string) => [v, name === 'totalIn' ? 'รับเข้า' : 'เบิกออก']} />
              <Legend formatter={(v) => v === 'totalIn' ? 'รับเข้า' : 'เบิกออก'} />
              <Bar dataKey="totalIn" fill="#0d9488" radius={[3, 3, 0, 0]} />
              <Bar dataKey="totalOut" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* ── Monthly Usage ── */}
      <Box mb="xl" style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
        <Group mb={16} gap={8}>
          <IconTrendingDown size={16} color="#0f172a" />
          <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t('stock.monthlyChart', 'การใช้งานรายเดือน (12 เดือนล่าสุด)')}</Text>
        </Group>
        {monthlyUsage === null ? <Skeleton height={180} /> : monthlyUsage.length === 0 ? (
          <Center py={40}><Text size="sm" c="dimmed">{t('common.noData', 'ยังไม่มีข้อมูล')}</Text></Center>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={monthlyUsage} margin={{ top: 5, right: 0, left: -30, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Tooltip formatter={(v: any, name: string) => [v, name === 'totalIn' ? 'รับเข้า' : 'เบิกออก']} />
              <Bar dataKey="totalIn" fill="#0d9488" radius={[3, 3, 0, 0]} />
              <Bar dataKey="totalOut" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* ── Cost by WO / HK ── */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* Cost by Work Order */}
        <Box style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
          <Group mb={12} gap={8}>
            <IconTicket size={16} color="#0f172a" />
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t('stock.costByWO', 'ต้นทุนวัสดุ — Work Order (30 วัน)')}</Text>
          </Group>
          {costWO === null ? <Skeleton height={200} /> : costWO.length === 0 ? (
            <Center py={30}><Text size="sm" c="dimmed">{t('common.noData', 'ยังไม่มีข้อมูล')}</Text></Center>
          ) : (
            <Stack gap={8}>
              {costWO.map((r: any) => (
                <Box key={r.itemId}>
                  <Group justify="space-between" mb={3}>
                    <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{r.name}</Text>
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">{fmt(r.totalQty)} {r.unit}</Text>
                      <Text size="xs" fw={600} c="#0f172a">{fmtCost(r.totalCost)}</Text>
                    </Group>
                  </Group>
                  <Progress value={Math.min((r.totalCost / maxCost) * 100, 100)} color="teal" size="xs" radius="xl" />
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Cost by HK Order */}
        <Box style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
          <Group mb={12} gap={8}>
            <IconBottle size={16} color="#0f172a" />
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t('stock.costByHK', 'ต้นทุนวัสดุ — HK Order (30 วัน)')}</Text>
          </Group>
          {costHK === null ? <Skeleton height={200} /> : costHK.length === 0 ? (
            <Center py={30}><Text size="sm" c="dimmed">{t('common.noData', 'ยังไม่มีข้อมูล')}</Text></Center>
          ) : (
            <Stack gap={8}>
              {costHK.map((r: any) => (
                <Box key={r.itemId}>
                  <Group justify="space-between" mb={3}>
                    <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{r.name}</Text>
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">{fmt(r.totalQty)} {r.unit}</Text>
                      <Text size="xs" fw={600} c="#0f172a">{fmtCost(r.totalCost)}</Text>
                    </Group>
                  </Group>
                  <Progress value={Math.min((r.totalCost / maxCost) * 100, 100)} color="grape" size="xs" radius="xl" />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  )
}
